/**
 * Words and rules of the budget optimizer panel in Ads Manager. The server decides what the owner may do: a button
 * exists only when its action is in the decision's `actions` list, and only for the owner of the ad campaign.
 */

export const MODES = Object.freeze({ OFF: 'off', MANUAL: 'manual', AUTOMATED: 'automated' })

export const ACTIONS = Object.freeze({
    APPLY: 'apply',
    DISMISS: 'dismiss',
    RETRY_RAISE: 'retry_raise',
    PUT_BACK: 'put_back',
    ACCEPT_LOWER_TOTAL: 'accept_lower_total',
    UNDO: 'undo',
    CHECK_AGAIN: 'check_again',
})

/** The values of the `BudgetPartialAction` enum for the actions that resolve a partly applied decision. */
export const PARTIAL_ACTION_VALUES = Object.freeze({
    [ACTIONS.RETRY_RAISE]: 'RETRY_RAISE',
    [ACTIONS.PUT_BACK]: 'PUT_BACK',
    [ACTIONS.ACCEPT_LOWER_TOTAL]: 'ACCEPT_LOWER_TOTAL',
})

export const MODE_LABELS = Object.freeze({
    [MODES.OFF]: 'Off',
    [MODES.MANUAL]: 'Manual',
    [MODES.AUTOMATED]: 'Automated',
})

export const MODE_HINTS = Object.freeze({
    [MODES.OFF]: 'Turba does not look for budget moves in this campaign.',
    [MODES.MANUAL]: 'Turba suggests a move between two ad sets. Nothing changes until you apply it.',
    [MODES.AUTOMATED]:
        'Turba moves budget between two ad sets by itself, within its limits. You can undo a move for 14 days.',
})

export const AUTOMATED_UNAVAILABLE_NOTE =
    'Automated mode is not switched on for your licence right now, so Turba only suggests moves and waits for you to apply them.'

export const HALTED_NOTE = 'Budget changes by the optimizer are paused by Turba. Nothing is changed until they resume.'

export const NOT_OFFERED_NOTE =
    'The budget optimizer is no longer offered for this campaign. Open decisions below can still be finished.'

export const READ_ONLY_NOTE = 'You can see the optimizer of this campaign but only its owner can change it.'

export const TOO_MUCH_DATA_NOTE = 'Too much data to evaluate this campaign'

/** Plain text for a reason the optimizer or the server gives; unknown reasons are shown as they are, readable. */
const REASON_TEXTS = Object.freeze({
    halted: 'The optimizer is paused by Turba.',
    not_available: 'The budget optimizer is not available for your licence.',
    automated_not_available: 'Automated mode is not available for your licence.',
    in_progress: 'Turba is changing this campaign’s budgets; try again in a few minutes.',
    busy: 'Another change to this campaign is being saved; try again in a moment.',
    try_again: 'Try again in a moment.',
    other_change_unconfirmed: 'Another change to this ad set is not confirmed yet; use Check again on the ad set.',
    expired: 'This suggestion has expired.',
    already_taken: 'This was already handled.',
    not_proposed: 'This suggestion is no longer open.',
    no_connection: 'Turba cannot read this campaign on Meta: the connection is missing or was removed.',
    campaign_budget: 'The optimizer does not handle campaigns with a campaign budget.',
    objective_not_supported: 'The optimizer does not handle this campaign objective.',
    deferred: 'This will be done a little later.',
    refused: 'This cannot be done right now.',
    // Why the last evaluation found nothing to move
    mode_off: 'The optimizer is off for this campaign.',
    no_candidate_pair: 'No two ad sets have enough data and a clear enough difference yet.',
    insufficient_results: 'Not enough results yet.',
    insufficient_spend: 'Not enough spend yet.',
    in_learning: 'An ad set is still learning.',
    recent_change: 'An ad set was changed recently.',
    decision_open: 'A move is waiting for a decision.',
    change_pending: 'A budget change is not confirmed yet.',
    insights_incomplete: 'Meta results could not be read completely.',
    ad_account_cooldown: 'The ad account is resting after too many requests.',
    needs_reauth: 'Reconnect your ad account.',
    currency_unsupported: 'This currency is not supported by the optimizer.',
    warm_up_not_finished: 'The pixel warm-up has not finished.',
    ad_set_gone: 'An ad set no longer exists on Meta.',
    live_changed: 'A budget was changed in Meta meanwhile.',
    not_delivering: 'The donor ad set is not delivering.',
    budget_change_limit_risk: 'Meta limits how often an ad set budget can change.',
})

export const reasonText = (reason) => {
    if (!reason) return ''
    return (
        REASON_TEXTS[reason] ||
        String(reason)
            .replace(/_/g, ' ')
            .replace(/^./, (letter) => letter.toUpperCase()) + '.'
    )
}

/** The text of a refused action: the server's message, else the words for its reason. */
export const refusalText = (result) =>
    result?.message || reasonText(result?.reason) || 'This could not be done. Try again.'

/** Which mode choices the owner has: manual and automated only while the optimizer is offered. */
export const modeChoices = (optimizer) => {
    if (!optimizer?.isOwner) return []
    const offered = optimizer.available === true
    return [
        { mode: MODES.OFF, label: MODE_LABELS.off, disabled: false },
        { mode: MODES.MANUAL, label: MODE_LABELS.manual, disabled: !offered },
        {
            mode: MODES.AUTOMATED,
            label: MODE_LABELS.automated,
            disabled: !offered || optimizer.automatedAvailable !== true,
        },
    ]
}

/** The note about the effective mode when it differs from the one the owner chose, or ''. */
export const effectiveModeNote = (optimizer) => {
    if (!optimizer) return ''
    if (optimizer.mode === MODES.AUTOMATED && optimizer.effectiveMode !== MODES.AUTOMATED) {
        return AUTOMATED_UNAVAILABLE_NOTE
    }
    return ''
}

/** Whether the panel is shown at all: when offered, or when a decision is open or can still be undone. */
export const panelVisible = (optimizer) => !!optimizer && (optimizer.available === true || !!optimizer.decision)

/** Whether the owner may take an action: it is in the server's list and the reader owns the campaign. */
export const may = (optimizer, action) =>
    optimizer?.isOwner === true && !!optimizer?.decision?.actions?.includes(action)

export const UNCONFIRMED_STATUSES = Object.freeze(['uncertain', 'applying', 'reverting'])

const changeOf = (decision, seq) => decision?.changes?.find((change) => change.seq === seq)

/** The ad set and budgets an unconfirmed change is about, for the banner. */
export const unconfirmedChange = (decision) =>
    decision?.changes?.find((change) => ['writing', 'unknown'].includes(change.status)) ||
    changeOf(decision, 2) ||
    changeOf(decision, 1) ||
    null

export const STATUS_TITLES = Object.freeze({
    proposed: 'Suggested budget move',
    applying: 'Budget move in progress',
    reverting: 'Undo in progress',
    partially_applied: 'Budget move only partly made',
    uncertain: 'Meta has not confirmed a budget change',
    needs_attention: 'Budget move needs your attention',
    applied: 'Budget move made',
})

/** Plain words for what a status means to the owner. */
export const statusText = (decision) => {
    if (!decision) return ''
    const revert = decision.kind === 'budget_revert'
    switch (decision.status) {
        case 'proposed':
            return 'Nothing changes on Meta until you apply it.'
        case 'applying':
        case 'reverting':
            return 'Turba is changing the budgets. This can take a few minutes.'
        case 'partially_applied':
            return revert
                ? 'The undo lowered one budget but did not raise the other, so the campaign total is lower than before. Retry the raise or accept the lower total.'
                : 'One budget was lowered but the other was not raised. Retry the raise or put the lowered budget back. Without a choice Turba puts it back after 24 hours.'
        case 'needs_attention':
            return 'Turba could not finish this by itself and waits for you. Check again, retry, or accept the lower total.'
        case 'applied':
            return 'You can undo this move while both budgets are still as Turba set them.'
        default:
            return ''
    }
}

/** The label of an action button; wording depends on the kind of decision and where it is. */
export const actionLabel = (action, decision) => {
    switch (action) {
        case ACTIONS.APPLY:
            return 'Apply'
        case ACTIONS.DISMISS:
            return 'Dismiss'
        case ACTIONS.UNDO:
            return 'Undo'
        case ACTIONS.CHECK_AGAIN:
            return 'Check again'
        case ACTIONS.ACCEPT_LOWER_TOTAL:
            return 'Accept lower total'
        case ACTIONS.RETRY_RAISE:
            return 'Retry raise'
        case ACTIONS.PUT_BACK:
            return decision?.status === 'needs_attention' ? 'Retry put back' : 'Put back'
        default:
            return action
    }
}

/** The order buttons are shown in. */
export const ACTION_ORDER = Object.freeze([
    ACTIONS.APPLY,
    ACTIONS.DISMISS,
    ACTIONS.RETRY_RAISE,
    ACTIONS.PUT_BACK,
    ACTIONS.ACCEPT_LOWER_TOTAL,
    ACTIONS.UNDO,
    ACTIONS.CHECK_AGAIN,
])

/** The actions to show as buttons, in order, from the server's flags only. */
export const visibleActions = (optimizer) =>
    optimizer?.isOwner === true ? ACTION_ORDER.filter((action) => optimizer.decision?.actions?.includes(action)) : []

/**
 * The banner for a change Meta has not confirmed, or for the campaign being changed right now; null otherwise.
 * `formatAmount` takes minor units.
 */
export const unconfirmedBanner = (decision, formatAmount) => {
    if (!decision) return null
    if (decision.status === 'uncertain') {
        const change = unconfirmedChange(decision)
        const what = change
            ? `the budget of ${change.adSetName || 'an ad set'} (${formatAmount(change.budgetBefore)} to ${formatAmount(change.budgetAfter)})`
            : 'a budget change'
        return `Meta did not confirm ${what}. Turba counts the higher budget until Meta confirms it and makes no new suggestions for this campaign.`
    }
    if (decision.status === 'applying' || decision.status === 'reverting') {
        return 'Turba is changing this campaign’s budgets. Budget edits of its ad sets are refused until it has finished.'
    }
    return null
}

/**
 * What to tell the owner after an action that moves budget, by the state the server answered with: only a finished
 * move is called done; one in progress or not yet confirmed says so.
 */
export const successText = (done, status) => {
    if (status === 'applying' || status === 'reverting') {
        return 'Turba is changing the budgets. This can take a few minutes: see the panel.'
    }
    if (['uncertain', 'partially_applied', 'needs_attention'].includes(status)) {
        return 'The budget move is not finished yet: see the panel for what to do next.'
    }
    return done
}
